import { Button } from "@/components/ui/button";
import { CalendarHeart, ArrowRight, Check } from "lucide-react";
import { SITE } from "@/config/site";

function SubHeading({ children }) {
  return (
    <div>
      <h3 className="font-heading text-xl font-semibold text-slate-900 tracking-tight">
        {children}
      </h3>
      <div className="gold-rule" />
    </div>
  );
}

export default function CoachingSection() {
  const c = SITE.coaching;

  return (
    <section
      id="coaching"
      className="py-20 sm:py-24"
      data-testid="coaching-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="paper-surface p-8 sm:p-12 lg:p-16 relative overflow-hidden">
          <div
            className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-br from-amber-100 via-primary/10 to-transparent blur-3xl"
            aria-hidden="true"
          />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 mb-5">
              <CalendarHeart className="h-4 w-4 text-primary" />
              <span className="text-xs font-semibold tracking-[0.15em] uppercase text-primary">
                {c.sectionEyebrow}
              </span>
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 max-w-3xl">
              {c.sectionHeadline}
            </h2>

            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed max-w-3xl">
              {c.subheadline}
            </p>

            <p className="mt-8 text-slate-700 leading-relaxed max-w-3xl">
              {c.intro}
            </p>

            {/* July Focus */}
            <div className="mt-10 max-w-3xl">
              <SubHeading>{c.julyFocus.title}</SubHeading>
              <div className="mt-3 space-y-3">
                {c.julyFocus.body.map((p, i) => (
                  <p key={i} className="text-slate-700 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </div>

            {/* Who this is for + What you'll leave with */}
            <div className="mt-10 grid md:grid-cols-2 gap-10">
              <div>
                <SubHeading>{c.whoFor.title}</SubHeading>
                <p className="mt-3 text-slate-700">{c.whoFor.lead}</p>
                <ul className="mt-3 space-y-2">
                  {c.whoFor.items.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-slate-700 leading-relaxed"
                    >
                      <Check className="h-4 w-4 mt-1 shrink-0 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <SubHeading>{c.leaveWith.title}</SubHeading>
                <p className="mt-3 text-slate-700 leading-relaxed">
                  {c.leaveWith.body}
                </p>
              </div>
            </div>

            {/* About Marie */}
            <div className="mt-10 max-w-3xl">
              <SubHeading>{c.aboutMarie.title}</SubHeading>
              <p className="mt-3 text-slate-700 leading-relaxed">
                {c.aboutMarie.body}
              </p>
            </div>

            {/* July dates + Summer special */}
            <div className="mt-10 grid md:grid-cols-2 gap-10">
              <div>
                <SubHeading>{c.julyDates.title}</SubHeading>
                <p className="mt-3 text-slate-700">{c.julyDates.lead}</p>
                <ul className="mt-3 space-y-2">
                  {c.julyDates.items.map((d, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-slate-700 leading-relaxed"
                    >
                      <CalendarHeart className="h-4 w-4 mt-1 shrink-0 text-primary" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <SubHeading>{c.summerSpecial.title}</SubHeading>
                <div className="mt-3 space-y-2 text-slate-700 leading-relaxed">
                  <p>{c.summerSpecial.regularRate}</p>
                  <p className="font-semibold text-primary">
                    {c.summerSpecial.julyRate}
                  </p>
                  <p className="text-sm text-slate-600">
                    {c.summerSpecial.note}
                  </p>
                </div>
              </div>
            </div>

            {/* After the session */}

            {/* CTA */}
            <div className="mt-12">
              <a
                href={c.ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="coaching-register-btn"
              >
                <Button
                  size="lg"
                  className="rounded-full px-8 py-6 text-base bg-primary hover:bg-teal-900"
                >
                  {c.ctaLabel}
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
